// Formant estimation by linear prediction (LPC), hand rolled: no library
// exists for the browser that does this well. Pipeline per frame:
//   decimate to ~12 kHz → pre-emphasis → Hamming window → autocorrelation
//   → Levinson-Durbin → polynomial roots → resonance frequencies + bandwidths.
// Formants are the resonances of the vocal tract; F1/F2 place the vowel and
// F3 (with the cluster above it) is most of what people call "ring".

export interface FormantEstimate {
  formants: number[];       // Hz, ascending, up to 4
  bandwidths: number[];     // Hz, same order
  fsUsed: number;           // decimated sample rate
}

export interface VowelGuess { symbol: string; keyword: string; distance: number; }

/** Rough gender-neutral targets (Hz), averaged from Hillenbrand et al. 1995 adult data. */
const VOWELS: { symbol: string; keyword: string; f1: number; f2: number }[] = [
  { symbol: 'i',  keyword: 'beet',   f1: 350, f2: 2500 },
  { symbol: 'ɪ',  keyword: 'bit',    f1: 450, f2: 2100 },
  { symbol: 'e',  keyword: 'bait',   f1: 480, f2: 2300 },
  { symbol: 'ɛ',  keyword: 'bet',    f1: 620, f2: 1950 },
  { symbol: 'æ',  keyword: 'bat',    f1: 720, f2: 1800 },
  { symbol: 'ɑ',  keyword: 'father', f1: 780, f2: 1200 },
  { symbol: 'ʌ',  keyword: 'but',    f1: 660, f2: 1300 },
  { symbol: 'ɔ',  keyword: 'bought', f1: 640, f2: 1000 },
  { symbol: 'o',  keyword: 'boat',   f1: 500, f2: 950  },
  { symbol: 'ʊ',  keyword: 'book',   f1: 480, f2: 1150 },
  { symbol: 'u',  keyword: 'boot',   f1: 370, f2: 950  },
  { symbol: 'ɝ',  keyword: 'bird',   f1: 500, f2: 1450 },
];

export const VOWEL_TABLE = VOWELS;

export function guessVowel(f1: number, f2: number): VowelGuess {
  let best = VOWELS[0], bestD = Infinity;
  for (const v of VOWELS) {
    // log-frequency distance: a 10% miss costs the same anywhere
    const d = Math.hypot(Math.log(f1 / v.f1), Math.log(f2 / v.f2) * 1.2);
    if (d < bestD) { bestD = d; best = v; }
  }
  return { symbol: best.symbol, keyword: best.keyword, distance: bestD };
}

/** Levinson-Durbin recursion: autocorrelation r[0..p] → predictor a[0..p] with a[0] = 1. */
function levinson(r: Float64Array, p: number): Float64Array | null {
  const a = new Float64Array(p + 1);
  const tmp = new Float64Array(p + 1);
  a[0] = 1;
  let err = r[0];
  if (err <= 0) return null;
  for (let i = 1; i <= p; i++) {
    let acc = r[i];
    for (let j = 1; j < i; j++) acc += a[j] * r[i - j];
    const k = -acc / err;
    tmp.set(a);
    for (let j = 1; j < i; j++) a[j] = tmp[j] + k * tmp[i - j];
    a[i] = k;
    err *= 1 - k * k;
    if (err <= 0) return null;
  }
  return a;
}

/** Roots of a real polynomial c[0] z^n + c[1] z^(n-1) + ... + c[n], Durand-Kerner. */
function polyRoots(c: Float64Array): { re: number; im: number }[] {
  const n = c.length - 1;
  const a = Array.from(c, v => v / c[0]);
  const re = new Float64Array(n), im = new Float64Array(n);
  // initial guesses on a circle
  for (let i = 0; i < n; i++) {
    const ang = (2 * Math.PI * i) / n + 0.4;
    re[i] = 0.9 * Math.cos(ang); im[i] = 0.9 * Math.sin(ang);
  }
  for (let iter = 0; iter < 60; iter++) {
    let maxDelta = 0;
    for (let i = 0; i < n; i++) {
      // evaluate polynomial at z_i (Horner)
      let pr = 1, pi = 0;
      for (let k = 1; k <= n; k++) {
        const nr = pr * re[i] - pi * im[i] + a[k];
        const ni = pr * im[i] + pi * re[i];
        pr = nr; pi = ni;
      }
      // denominator: product of (z_i - z_j)
      let dr = 1, di = 0;
      for (let j = 0; j < n; j++) {
        if (j === i) continue;
        const xr = re[i] - re[j], xi = im[i] - im[j];
        const nr = dr * xr - di * xi;
        const ni = dr * xi + di * xr;
        dr = nr; di = ni;
      }
      const den = dr * dr + di * di;
      if (den === 0) continue;
      const qr = (pr * dr + pi * di) / den;
      const qi = (pi * dr - pr * di) / den;
      re[i] -= qr; im[i] -= qi;
      const d = Math.abs(qr) + Math.abs(qi);
      if (d > maxDelta) maxDelta = d;
    }
    if (maxDelta < 1e-9) break;
  }
  const out = [];
  for (let i = 0; i < n; i++) out.push({ re: re[i], im: im[i] });
  return out;
}

/**
 * Estimate formants from a time-domain frame. Returns null on silence or if
 * the predictor is unstable. `order` defaults to 2 + fs_dec/1000.
 */
export function estimateFormants(
  buffer: Float32Array, sampleRate: number,
  opts: { minHz?: number; maxHz?: number; maxBandwidth?: number; order?: number } = {},
): FormantEstimate | null {
  const minHz = opts.minHz ?? 90;
  const maxHz = opts.maxHz ?? 5000;
  const maxBw = opts.maxBandwidth ?? 400;

  // 1. Decimate to roughly 12 kHz with a box low-pass (voice has little above 6 kHz that LPC needs)
  const factor = Math.max(1, Math.round(sampleRate / 12000));
  const fs = sampleRate / factor;
  const n = Math.floor(buffer.length / factor);
  if (n < 256) return null;
  const x = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    let s = 0;
    for (let k = 0; k < factor; k++) s += buffer[i * factor + k];
    x[i] = s / factor;
  }

  // silence gate
  let rms = 0;
  for (let i = 0; i < n; i++) rms += x[i] * x[i];
  rms = Math.sqrt(rms / n);
  if (rms < 0.004) return null;

  // 2. Pre-emphasis + Hamming window
  const w = new Float64Array(n);
  for (let i = n - 1; i > 0; i--) w[i] = (x[i] - 0.97 * x[i - 1]) * (0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (n - 1)));
  w[0] = 0;

  // 3. Autocorrelation
  const p = opts.order ?? Math.round(2 + fs / 1000);
  const r = new Float64Array(p + 1);
  for (let lag = 0; lag <= p; lag++) {
    let s = 0;
    for (let i = lag; i < n; i++) s += w[i] * w[i - lag];
    r[lag] = s;
  }
  if (r[0] <= 0) return null;

  // 4. LPC
  const a = levinson(r, p);
  if (!a) return null;

  // 5. Roots → resonances
  const roots = polyRoots(a);
  const cands: { f: number; bw: number }[] = [];
  for (const { re, im } of roots) {
    if (im <= 1e-6) continue; // one of each conjugate pair
    const f = (Math.atan2(im, re) * fs) / (2 * Math.PI);
    const mag = Math.hypot(re, im);
    if (mag >= 1 || mag <= 0) continue;
    const bw = (-Math.log(mag) * fs) / Math.PI;
    if (f < minHz || f > maxHz || bw > maxBw) continue;
    cands.push({ f, bw });
  }
  cands.sort((u, v) => u.f - v.f);
  // Merge resonances closer than 150 Hz (double roots on one formant)
  const merged: { f: number; bw: number }[] = [];
  for (const c of cands) {
    const last = merged[merged.length - 1];
    if (last && c.f - last.f < 150) { if (c.bw < last.bw) merged[merged.length - 1] = c; }
    else merged.push(c);
  }
  if (merged.length === 0) return null;
  return {
    formants: merged.slice(0, 4).map(c => c.f),
    bandwidths: merged.slice(0, 4).map(c => c.bw),
    fsUsed: fs,
  };
}
